import React, { useContext } from 'react';
import { useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import { type InlineEditorHints } from '@bbk/ui/inline-editing';
import { type RestSimpleLinkLaCard } from '@bbkAdminUtils/api-client/rest-la-cards-types';
import { type RestModalForwards } from '@bbkAdminUtils/api-client/rest-modals-cards-types';
import { cs } from '@bbkAdminUtils/css-utils';
import { useDetectIE } from '@bbkAdminUtils/detect-ie';
import { type EmotionTheme } from '@bbkFunnel/pages/theme';
import CustomerHtml from '@bbkFunnelComponents/customer_html/customer_html';
import { FunnelContext } from '@bbkFunnelComponents/FunnelContext';
import { LossGridItem } from '@bbkFunnelComponents/loss/sections/grid-item';
import { LossTitle } from '@bbkFunnelComponents/loss/sections/title';
import ActionButtonIcons from '@bbkFunnelComponents/modals/sections/action-button-helpers';
import { OtherActions } from '@bbkFunnelComponents/modals/sections/other-actions';
import { FunnelButton } from '@bbkFunnelComponents/v2';
import { isSVGLink } from '@bbkFunnelUtils/shared';
import { makeClassNameSafe } from '@bbkFunnelUtils/utils';

import './loss-simple-link.scss';

type Props = {
  card: RestSimpleLinkLaCard & {
    style_overlay?: {
      fillIconColor?: string;
      light_background?: string;
      chip_value?: string;
      button_forwards?: {
        backgroundColor?: string;
      };
    };
  };
  onClick?: () => void;
  handleLossCardAction?: ({ action }: { action: RestModalForwards }) => void;
  isHorizontal: boolean;
};

const LossGridItemOuterContainer = styled.div<{ isHorizontal: boolean }>`
  display: flex;
  flex-direction: ${({ isHorizontal }) => (isHorizontal ? 'row' : 'column')};
`;

const LossGridItemInnerContainer = styled.div<{ isHorizontal: boolean }>`
  display: flex;
  flex-direction: column;
  width: 100%;
  ${({ isHorizontal }) => (isHorizontal ? 'padding: 30px;' : '')}
`;

const RenderLossSimpleLink: React.FC<Props> = ({
  card,
  card: {
    max_redemptions_reached,
    id,
    requires: { help_text, image, copy, title, vertical, forwards },
  },
  handleLossCardAction,
  onClick,
  isHorizontal,
}) => {
  const { isPreview } = useContext(FunnelContext);
  const isIE = useDetectIE();
  const theme = useTheme() as EmotionTheme;

  const isVertical = !isHorizontal && vertical;
  const isRegular = !isHorizontal && !isVertical;

  const buttonForwards = card?.style_overlay?.button_forwards;
  const backgroundColor = buttonForwards?.backgroundColor;

  const specificStyles = {
    backgroundColor,
  };
  const equalButtonSize = theme.complianceEqualButtonSize;

  const renderedImage =
    image &&
    (isVertical ? (
      <div data-part="image">
        <img srcSet={`${image} 2x`} alt="" />
      </div>
    ) : (
      <div className="bkk-sl-sc-image" data-part="image">
        {isSVGLink(image) && !isIE ? (
          <div
            className="bkk-image-svg bkk-sl-sc-image-svg"
            css={{
              backgroundImage: `url(${image})`,
            }}
          />
        ) : (
          <img src={image} alt="" css={{ minWidth: 120 }} />
        )}
      </div>
    ));
  return (
    <LossGridItem
      classes={cs(
        'simple-link bbk-simple-link-survey-card bbk-loss-simple-link-grid-item bbk-loss-simple-link-grid-item-simple-link',
        makeClassNameSafe(card.name),
        {
          'bbk-simple-link-survey-card-horizontal': !isVertical,
          'bbk-simple-link-survey-card-vertical': isVertical,
        }
      )}
      onClick={isPreview ? onClick : () => undefined}
      isHorizontal={isHorizontal}
    >
      <LossGridItemOuterContainer isHorizontal={isHorizontal}>
        {!isRegular && renderedImage}

        <LossGridItemInnerContainer isHorizontal={isHorizontal}>
          <LossTitle>
            <span data-part="title">
              <CustomerHtml value={title} />
            </span>
          </LossTitle>
          <div
            className="d-flex bbk-slsc-copy-container"
            css={{ flex: 1, alignItems: 'center' }}
          >
            <div className="bbk-slsc-left-copy-container">
              <div className="copy">
                <CustomerHtml value={copy} />
              </div>
              {help_text && <div className="subtle-help-text">{help_text}</div>}
            </div>
            {isRegular && renderedImage}
          </div>
          {forwards && (
            <div
              className={cs('btn', {
                'tw-flex tw-justify-center': equalButtonSize,
              })}
              data-part="action"
            >
              <FunnelButton
                disabled={max_redemptions_reached}
                onClick={() =>
                  handleLossCardAction?.({ action: card.requires.forwards })
                }
                specificStyles={specificStyles}
                classes="d-flex align-items-center justify-content-center"
              >
                {forwards?.icon && (
                  <ActionButtonIcons
                    styles={{ marginRight: 4 }}
                    icon={forwards.icon}
                  />
                )}
                {forwards.text}
              </FunnelButton>
            </div>
          )}
          {card.requires.other_actions && (
            <OtherActions
              version2Template
              id={id}
              otherActions={card.requires.other_actions}
              onClick={(action) => handleLossCardAction?.({ action })}
            />
          )}
        </LossGridItemInnerContainer>
      </LossGridItemOuterContainer>
    </LossGridItem>
  );
};

const LossSimpleLink = Object.assign(RenderLossSimpleLink, {
  editorHints: {
    title: {
      path: ['requires'],
      control: 'group',
      selector: '[data-part="title"]',
      placement: 'top-end',
      fields: [
        {
          path: ['title'],
          control: 'richText',
        },
        {
          path: ['copy'],
          control: 'richText',
          label: 'Text',
        },
      ],
    },
    image: {
      path: ['requires'],
      control: 'group',
      selector: '[data-part="image"]',
      placement: 'top-end',
      fields: [
        {
          path: ['image'],
          control: 'image',
          options: {
            helpLabel: 'Recommended max width: 150px',
          },
        },
        {
          path: ['vertical'],
          control: 'boolean',
          label: 'Vertical layout',
        },
      ],
    },
    action: {
      path: ['requires', 'forwards'],
      control: 'ctaAppearance',
      selector: '[data-part="action"]',
      placement: 'top-end',
      label: 'Action',
    },
    otherActions: {
      path: ['requires', 'other_actions'],
      control: 'ctaAppearance',
      selector: '[data-part="other-action"]',
      placement: 'top-end',
      label: 'Action',
      indexAttribute: 'data-action-index',
    },
  } satisfies InlineEditorHints,
});
export default LossSimpleLink;
